/**
 * Invite request list.
 *
 * The status filter and the page number both live in the query string, so any
 * view can be linked to directly. A row keeps the applicant's identity and state
 * on one line and opens to the full reason plus the way into the detail page,
 * which is what keeps twenty records and both pagers within one screen: reaching
 * the next page never means scrolling past the whole list first.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LoadingBlock, ErrorBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Chip, ChipGroup } from '../../components/ui/chip'
import { EmptyState } from '../../components/ui/primitives'
import { useAdminApplications } from '../../hooks/use-admin'
import { formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, CollapsibleRow, Pager, StateBadge, applicationVariant, usePageParam } from './shared'

const FILTERS = ['pending', 'approved', 'rejected', 'withdrawn', 'all'] as const

export function AdminApplicationsPage() {
  const { t } = useTranslation()
  const { page, params, update, goToPage } = usePageParam()
  const status = params.get('status') ?? 'pending'
  const locale = currentLocale()

  const applications = useAdminApplications(status, page)

  function select(next: string) {
    update({ status: next, page: '1' })
  }

  const pager = applications.data ? (
    <Pager
      page={applications.data.page}
      pageSize={applications.data.pageSize}
      total={applications.data.total}
      onPage={goToPage}
    />
  ) : null

  return (
    <AdminPage
      title={t('admin.applications.title')}
      description={t('admin.applications.description')}
      current={t('admin.nav.applications')}
    >
      <ChipGroup label={t('admin.applications.filterLabel')}>
        {FILTERS.map((filter) => (
          <Chip key={filter} selected={status === filter} onClick={() => select(filter)}>
            {t(`admin.applicationStatus.${filter}`)}
          </Chip>
        ))}
      </ChipGroup>

      {applications.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {applications.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void applications.refetch()}
        />
      ) : null}

      {applications.data && applications.data.items.length === 0 ? (
        <EmptyState title={t('admin.applications.empty')} />
      ) : null}

      {pager}

      <div className="flex flex-col gap-2">
        {applications.data?.items.map((item) => (
          <CollapsibleRow
            key={item.id}
            title={<span className="truncate text-sm font-medium">{item.email}</span>}
            meta={
              <span className="text-muted-foreground truncate text-xs">
                {item.nickname ?? t('admin.users.nicknameEmpty')} ·{' '}
                {t('admin.applications.submittedAt', {
                  time: formatUnixSeconds(item.createdAt, locale),
                })}
              </span>
            }
            badges={
              <>
                <StateBadge
                  label={t(`admin.applicationStatus.${item.status}`)}
                  variant={applicationVariant(item.status)}
                />
                {item.isInvited ? (
                  <StateBadge label={t('admin.users.activated')} variant="success" />
                ) : null}
              </>
            }
          >
            {item.reason ? (
              <p className="text-sm break-words whitespace-pre-wrap">{item.reason}</p>
            ) : (
              <p className="text-muted-foreground text-xs">{t('admin.applications.noReason')}</p>
            )}
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/admin/applications/${item.id}`}>{t('admin.applications.open')}</Link>
              </Button>
            </div>
          </CollapsibleRow>
        ))}
      </div>

      {pager}
    </AdminPage>
  )
}
